// Safe Bootstrap (Tier A) — bảo vệ folder user khỏi mất changes uncommitted khi
// `avatar init` modify .gitignore + tạo initial commit.
//
// Flow:
//   1. detectFolderGitState() → "empty" | "untracked-only" | "clean" | "dirty"
//   2. empty/clean → bootstrap thẳng (không có rủi ro)
//   3. dirty/untracked-only → promptBootstrapStrategy() → 4 lựa chọn
//   4. executeBootstrapWithStrategy() → branch xử lý từng strategy
//
// 4 strategies:
//   - stash (DEFAULT highlight): git stash -u → bootstrap → stash pop
//   - commit-all (legacy v1.1.6): git add . && commit, all changes bundled
//   - skip: throw InitAbortedByUserError — user tự commit rồi chạy lại
//   - branch: checkout -b avatar/init → bootstrap → checkout back
//
// CLI flags:
//   --bootstrap-strategy <s>  : preset, skip prompt
//   --preserve-uncommitted    : alias cho --bootstrap-strategy=stash
//   --yes                     : CI mode → default stash (safest)

import { readdirSync } from "node:fs";
import { type SimpleGit, simpleGit } from "simple-git";
import { appendAuditEntry } from "./audit-log-appender.js";
import { checkFolderHasGit } from "./check-folder-has-git.js";
import { createInitialGitCommit } from "./create-initial-git-commit.js";
import { detectFolderTechStack } from "./detect-folder-tech-stack.js";
import { composeGitignoreContent } from "./gitignore-template-loader.js";
import { selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";
import { writeOrMergeGitignore } from "./write-or-merge-gitignore.js";

const BOOTSTRAP_FLAG_SPEC = {
  flag: "--bootstrap-strategy",
  command: "add repo",
  hint: "--bootstrap-strategy stash|commit-all|skip|branch",
};

export type GitState = "empty" | "untracked-only" | "clean" | "dirty";

export type BootstrapStrategy = "stash" | "commit-all" | "skip" | "branch";

export interface SafeBootstrapOptions {
  presetStrategy?: BootstrapStrategy;
  autoYes?: boolean;
}

// User chọn skip giữa wizard → caller catch để abort init.ts gracefully.
export class InitAbortedByUserError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InitAbortedByUserError";
  }
}

// === Step 1: detect git state ===
export async function detectFolderGitState(folderPath: string): Promise<GitState> {
  const hasGit = checkFolderHasGit(folderPath);
  if (!hasGit) {
    // Filter `.git` (rare gitlink file edge case) + dotfiles không count cho empty check.
    const entries = readdirSync(folderPath).filter((e) => e !== ".git");
    return entries.length === 0 ? "empty" : "untracked-only";
  }
  const g = simpleGit({ baseDir: folderPath });
  const status = await g.status();
  return status.isClean() ? "clean" : "dirty";
}

// === Step 2: prompt strategy ===
export async function promptBootstrapStrategy(
  state: GitState,
  opts: SafeBootstrapOptions,
): Promise<BootstrapStrategy> {
  // Preset flag → skip prompt hoàn toàn.
  if (opts.presetStrategy) return opts.presetStrategy;

  // CI mode (--yes) → safe default. Stash là an toàn nhất (reversible).
  if (opts.autoYes) return "stash";

  // Empty/clean state không gọi prompt (caller skip). Defensive return.
  if (state === "empty" || state === "clean") return "commit-all";

  // ⛔ LỖ L2: site này hiện là DEAD CODE — `safeBootstrapGitInFolder` chỉ có caller
  // trong test, không lệnh nào gọi. KHÔNG khai cờ CLI mới cho nó (cờ không có site
  // thì ca âm của nó xanh giả vĩnh viễn). Nhưng vẫn bọc qua `selectOrFlag` để nếu
  // mai này ai nối nó vào một lệnh thật thì nó thoát ≠0 chứ không treo im lặng.
  return (await selectOrFlag(opts.presetStrategy, BOOTSTRAP_FLAG_SPEC, {
    message:
      state === "dirty"
        ? "Folder có changes chưa commit. Cách xử lý:"
        : "Folder có file chưa version. Cách xử lý:",
    choices: [
      {
        value: "stash" as const,
        name: "1. Stash changes → bootstrap → restore (KHUYẾN NGHỊ)",
      },
      {
        value: "commit-all" as const,
        name: "2. Commit toàn bộ vào initial commit (legacy v1.1.6)",
      },
      {
        value: "skip" as const,
        name: "3. Skip — tôi commit thủ công rồi chạy lại",
      },
      {
        value: "branch" as const,
        name: "4. Commit vào branch riêng `avatar/init` (main giữ sạch)",
      },
    ],
    default: "stash",
  })) as BootstrapStrategy;
}

// === Helpers: stash + branch ===

// Stash user changes (kể cả untracked) với name rõ ràng để track. Trả true nếu stash thật sự.
async function stashUserChanges(g: SimpleGit, stashName: string): Promise<boolean> {
  const status = await g.status();
  if (status.isClean() && status.not_added.length === 0) return false;
  await g.stash(["push", "--include-untracked", "-m", stashName]);
  log.info(`Stashed changes: ${stashName}`);
  return true;
}

// Restore stash. Nếu conflict → log warning + leave markers (KHÔNG tự revert).
async function restoreStash(g: SimpleGit, stashName: string): Promise<void> {
  try {
    await g.stash(["pop"]);
    log.success(`Restored stash: ${stashName}`);
  } catch (err) {
    log.warn(
      "Restore stash conflict — files có Avatar tạo và stash của user xung đột. Stash giữ tại ref stash@{0}.",
    );
    log.warn("Resolve: git stash show -p stash@{0} → fix conflict → git stash drop");
    log.dim(`Detail: ${(err as Error).message}`);
  }
}

// Lấy branch hiện tại (HEAD). Fallback "main" nếu detached HEAD.
async function getCurrentBranch(g: SimpleGit): Promise<string> {
  try {
    const result = await g.revparse(["--abbrev-ref", "HEAD"]);
    const branch = result.trim();
    return branch === "HEAD" ? "main" : branch;
  } catch {
    return "main";
  }
}

// === Step 3: execute strategy ===
async function writeAvatarGitignore(folderPath: string): Promise<void> {
  const stacks = detectFolderTechStack(folderPath);
  log.info(`Tech stack: ${stacks.join(", ")}`);
  writeOrMergeGitignore(folderPath, composeGitignoreContent(stacks));
  log.success(".gitignore đã ghi (Avatar block)");
}

export async function executeBootstrapWithStrategy(
  folderPath: string,
  strategy: BootstrapStrategy,
): Promise<void> {
  const g = simpleGit({ baseDir: folderPath });

  switch (strategy) {
    case "skip":
      throw new InitAbortedByUserError(
        "Init aborted. Commit thủ công changes hiện tại rồi chạy lại `avatar init`.",
      );

    case "stash": {
      const stashName = `avatar-init-backup-${Date.now()}`;
      // Stash cần initial commit để work. Nếu folder chưa có .git hoặc chưa có commit
      // → init + empty commit baseline trước, sau đó mới stash user changes.
      const hadGit = checkFolderHasGit(folderPath);
      if (!hadGit) {
        await g.init();
        await g.branch(["-M", "main"]).catch(() => undefined);
      }
      const hasCommit = (await g.raw(["rev-list", "-n", "1", "--all"]).catch(() => "")).trim();
      if (!hasCommit) {
        await g.commit("chore: avatar baseline (pre-stash)", undefined, { "--allow-empty": null });
      }
      const stashed = await stashUserChanges(g, stashName);
      try {
        await writeAvatarGitignore(folderPath);
        await createInitialGitCommit(folderPath);
      } finally {
        // Always restore stash dù bootstrap fail giữa chừng.
        if (stashed) await restoreStash(g, stashName);
      }
      break;
    }

    case "commit-all": {
      // Legacy v1.1.6 behavior — KHÔNG dùng cho default mới nhưng giữ cho backward compat.
      await writeAvatarGitignore(folderPath);
      await createInitialGitCommit(folderPath);
      break;
    }

    case "branch": {
      // Cần repo trước. Nếu chưa có git thì init + commit baseline ở main đầu tiên.
      const hadGit = checkFolderHasGit(folderPath);
      if (!hadGit) {
        await g.init();
        await g.branch(["-M", "main"]);
      }
      const originalBranch = await getCurrentBranch(g);
      // checkoutLocalBranch fail nếu branch đã tồn tại — fallback checkout existing.
      try {
        await g.checkoutLocalBranch("avatar/init");
      } catch {
        await g.checkout("avatar/init");
      }
      await writeAvatarGitignore(folderPath);
      await createInitialGitCommit(folderPath);
      // Switch back. Nếu fail (vd no commits ở branch gốc) → ở lại avatar/init.
      try {
        await g.checkout(originalBranch);
        log.info(
          `Avatar init committed ở branch 'avatar/init'. Switch back về '${originalBranch}'. Merge khi sẵn sàng: git merge avatar/init`,
        );
      } catch {
        log.warn(
          `Không switch về '${originalBranch}' được — ở lại branch 'avatar/init'. Switch tay sau.`,
        );
      }
      break;
    }
  }
}

// === Main orchestrator (thay bootstrapGitInFolder cho dirty/untracked case) ===
export async function safeBootstrapGitInFolder(
  folderPath: string,
  opts: SafeBootstrapOptions = {},
): Promise<void> {
  const state = await detectFolderGitState(folderPath);
  log.info(`Folder state: ${state}`);

  // Empty/clean → bootstrap thẳng, không prompt.
  if (state === "empty" || state === "clean") {
    await writeAvatarGitignore(folderPath);
    if (state === "empty") {
      await createInitialGitCommit(folderPath);
    }
    await appendAuditEntry("bootstrap", `state=${state},strategy=auto`);
    return;
  }

  // Dirty/untracked → prompt + execute.
  const strategy = await promptBootstrapStrategy(state, opts);
  await executeBootstrapWithStrategy(folderPath, strategy);
  await appendAuditEntry("bootstrap", `state=${state},strategy=${strategy}`);
}
